import { Box, Button, TextField } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { countryNames } from './constants';
import { TemplateCreateForm } from './template-create-form';
import { TemplateCreateFromBaseForm } from './template-create-from-base';
import { TemplateReloadButton } from './template-reload';
import { List } from 'components/reusable/list';
import { ReusableSelect as Select } from 'components/reusable/select';
import { useGlobalState } from 'store/reducers/reducer';
import { loadTemplates } from 'store/sagas/sagas';
import './template-manager.css';
import { useDebounce } from 'utils/hooks';
import { pushFiltersSearchPath, rawParseQuery, transformQueryTypes } from 'utils/url';
const filterOptions = {
    pageSize: [
        { id: 10, name: '10' },
        { id: 25, name: '25' },
        { id: 50, name: '50' },
        { id: 100, name: '100' },
        { id: 250, name: '250' },
        { id: 500, name: '500' }
    ]
};
const initialData = {
    page: 1,
    pageSize: 50,
    version: '',
    country: '',
    state: '',
    type: '',
    id: '',
    active: ''
};
const mapData = {
    page: 'number',
    pageSize: 'number',
    version: 'string',
    country: 'string',
    state: 'string',
    type: 'string',
    id: 'string',
    active: 'string'
};
export const TemplateManager = () => {
    const { asyncDispatch } = useGlobalState();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [totalTemplates, setTotalTemplates] = useState(0);
    const qsData = transformQueryTypes(mapData, rawParseQuery());
    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [filter, setFilter] = useState({
        ...initialData,
        ...qsData
    });
    const debouncedFilter = useDebounce(filter, 250);
    const handleGoToTemplate = useCallback((templateId) => {
        navigate(`/admin/templates/${templateId}`);
    }, [navigate]);
    const handlePageChange = (_event, page) => {
        updateFilter({
            ...filter,
            page
        });
    };
    useEffect(() => {
        const { isInitial } = pushFiltersSearchPath({
            filter: debouncedFilter,
            initialData: initialData,
            mapData,
            path: pathname
        });
        if (!isInitial) {
            setIsOpenFilters(true);
        }
        setLoading(true);
        asyncDispatch(loadTemplates(debouncedFilter))
            .then((res) => {
            setTotalTemplates(res.total);
            setTemplates(res.templates);
            setLoading(false);
        }, console.error)
            .finally(() => {
            setLoading(false);
        });
    }, [debouncedFilter]);
    const updateFilter = (newFilter) => {
        setFilter({ ...initialData, ...newFilter });
    };
    const handleChangeFilters = (e) => {
        updateFilter({
            ...filter,
            [e.target.name]: e.target.value
        });
    };
    const columns = [
        {
            xs: 1,
            name: 'Id',
            id: 'id'
        },
        {
            xs: 2,
            name: 'Type',
            id: 'type'
        },
        {
            xs: 1,
            name: 'Country',
            customRenderValue: ({ country }) => `${countryNames[country]} (${country})`,
            id: 'country'
        },
        {
            xs: 1,
            name: 'State',
            id: 'state'
        },
        {
            xs: 1,
            name: 'Version',
            id: 'version'
        },
        {
            xs: 2,
            name: 'Created',
            id: 'createdAt'
        },
        {
            xs: 2,
            name: 'Updated',
            id: 'updatedAt'
        },
        {
            xs: 1,
            name: 'Active',
            id: 'active'
        }
    ];
    return (React.createElement("div", { className: "template-manager-container" },
        React.createElement(TemplateCreateForm, null),
        React.createElement(TemplateCreateFromBaseForm, null),
        React.createElement(Box, { margin: "16px" },
            React.createElement(TemplateReloadButton, null)),
        React.createElement("div", { className: "filters__container" },
            React.createElement("div", { className: "filters", onClick: () => {
                    setIsOpenFilters(!isOpenFilters);
                } },
                React.createElement(FilterListIcon, { style: {
                        color: isOpenFilters ? '#4122af' : 'grey'
                    } }),
                React.createElement("span", null, "Filter")),
            isOpenFilters && (React.createElement("div", { className: "jobs__filters-container-container" },
                React.createElement("div", { className: "jobs-filters-container u-padding-top-medium u-margin-bottom-large" },
                    React.createElement("div", { className: "u-margin-bottom-large" },
                        React.createElement(Select, { className: "g-input-width-16", items: filterOptions.pageSize, label: "Page Size", name: "pageSize", onChange: handleChangeFilters, value: filter.pageSize }),
                        React.createElement(TextField, { className: "u-margin-bottom-small g-input-width-16", label: "Id", name: "id", onChange: handleChangeFilters, value: filter.id }),
                        React.createElement(TextField, { className: "u-margin-bottom-small g-input-width-16", label: "Version", name: "version", onChange: handleChangeFilters, value: filter.version }),
                        React.createElement(TextField, { className: "u-margin-bottom-small g-input-width-16", label: "State", name: "state", onChange: handleChangeFilters, value: filter.state }),
                        React.createElement(TextField, { className: "u-margin-bottom-small g-input-width-16", label: "Country", name: "country", onChange: handleChangeFilters, value: filter.country }),
                        React.createElement(TextField, { className: "u-margin-bottom-small g-input-width-16", label: "Type", name: "type", onChange: handleChangeFilters, value: filter.type }),
                        React.createElement(Select, { className: "u-margin-bottom-small g-input-width-16", items: [
                                { id: 'true', name: 'true' },
                                { id: 'false', name: 'false' }
                            ], label: "Active", name: "active", onChange: handleChangeFilters, value: filter.active })),
                    React.createElement("div", { className: "u-margin-bottom-large" },
                        React.createElement(Button, { color: "primary", onClick: () => {
                                updateFilter(initialData);
                            } }, "Clear")))))),
        React.createElement("div", { className: "template-grid" },
            React.createElement(List, { columns: columns, handlePageChange: handlePageChange, isClickable: () => true, items: templates, label: "Templates", loading: loading, onClick: (item) => {
                    handleGoToTemplate(item.id);
                }, page: filter.page, total: totalTemplates, totalPages: Math.ceil(totalTemplates / filter.pageSize) }))));
};
